import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { CalendarBlank } from "@phosphor-icons/react";
import { toLocalISODate } from "../lib/dateUtil";

const pad2 = (n) => String(n).padStart(2, "0");

function isoToText(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

function isoToDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
}

function textToISO(text) {
  const t = (text || "").trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  const uk = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  const [y, mo, d] = iso ? [iso[1], iso[2], iso[3]] : uk ? [uk[3], uk[2], uk[1]] : [];
  if (!y) return null;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (dt.getFullYear() !== Number(y) || dt.getMonth() !== Number(mo) - 1 || dt.getDate() !== Number(d)) return null;
  return `${y}-${pad2(mo)}-${pad2(d)}`;
}

function maskTyping(raw) {
  if (/^\d{4}-/.test(raw)) return raw.slice(0, 10);
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter((p, i) => p || i === 0);
  return parts.join("/");
}

/* Drop-in replacement for <Input type="date">: value/onChange stay ISO yyyy-mm-dd, the user sees and types dd/mm/yyyy,
   with a calendar picker. Emits onChange({ target: { value, name } }) exactly like the native input. */
export default function DateInput({ value, onChange, min, max, disabled, className = "", placeholder = "dd/mm/yyyy", name, id, required, "data-testid": testid, ...rest }) {
  const [text, setText] = useState(isoToText(value));
  const [open, setOpen] = useState(false);
  const focused = useRef(false);

  useEffect(() => { if (!focused.current) setText(isoToText(value)); }, [value]);

  const emit = (iso) => onChange?.({ target: { value: iso, name, id }, currentTarget: { value: iso, name, id } });

  const handleType = (e) => {
    const t = maskTyping(e.target.value);
    setText(t);
    if (!t) { emit(""); return; }
    const iso = textToISO(t);
    if (iso) emit(iso);
  };

  const handleBlur = () => {
    focused.current = false;
    if (!text.trim()) { if (value) emit(""); return; }
    const iso = textToISO(text);
    if (iso) { setText(isoToText(iso)); if (iso !== value) emit(iso); } else setText(isoToText(value));
  };

  return (
    <div className={`relative ${className}`} data-testid={testid ? `${testid}-wrap` : undefined}>
      <Input
        type="text" inputMode="numeric" autoComplete="off"
        value={text} placeholder={placeholder} disabled={disabled} required={required} name={name} id={id}
        onFocus={() => { focused.current = true; }}
        onChange={handleType} onBlur={handleBlur}
        className="pr-9 font-mono" data-testid={testid} {...rest}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Open calendar" tabIndex={-1}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
            data-testid={testid ? `${testid}-calendar-btn` : undefined}>
            <CalendarBlank size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single" weekStartsOn={1}
            selected={isoToDate(value)} defaultMonth={isoToDate(value) || new Date()}
            fromDate={isoToDate(min)} toDate={isoToDate(max)}
            onSelect={(d) => { if (d) { const iso = toLocalISODate(d); setText(isoToText(iso)); emit(iso); } setOpen(false); }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
